import React, { useMemo, useState } from "react";
import { Card, Table, Button, Form, Row, Col, Alert } from "react-bootstrap";
import Layout from "../../layout/Layout";

const DENSITY = 7.85;

const PAKET_ICI_MAP = {
  37: 7,
  61: 9,
  91: 11,
  127: 13,
  169: 15,
  217: 17,
  271: 19,
  331: 21,
  397: 23,
};

const DEFAULT_LIMITS = {
  maxTonLimit: 25000, // kg
  maxBoyLimit: 12000, // mm
};

const createEmptyRow = () => ({
  id: Date.now() + Math.random(),
  kalinlik: "",
  ebat: "",
  boy: "",
  paketIci: "",
  bantEni: "",
  adet: "",
  enAdet: 5,
  boyAdet: 5,
});

function toNum(value) {
  if (value === "" || value === null || value === undefined) return 0;
  const normalized = String(value).replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function round(value, digits = 2) {
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

function formatTr(value, digits = 0) {
  if (!Number.isFinite(value)) return "";
  return value.toLocaleString("tr-TR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function calculateBaseRow(row) {
  const kalinlik = toNum(row.kalinlik);
  const ebat = toNum(row.ebat);
  const boy = toNum(row.boy);
  const paketIci = toNum(row.paketIci);
  const bantEni = toNum(row.bantEni);
  const adet = toNum(row.adet);
  const enAdet = toNum(row.enAdet) || 5;
  const boyAdet = toNum(row.boyAdet) || 5;

  const paketKgRaw =
    kalinlik > 0 && bantEni > 0 && boy > 0 && paketIci > 0
      ? (DENSITY * kalinlik * bantEni * boy * paketIci) / 1000000
      : 0;

  const paketKg = round(paketKgRaw, 0);

  const paketSayisiRaw = paketIci > 0 ? adet / paketIci : 0;
  const paketSayisi = Math.floor(paketSayisiRaw);
  const kalanPaket = paketIci > 0 ? adet - paketSayisi * paketIci : 0;

  const totalTons = round((paketKgRaw * paketSayisi) / 1000, 1);

  const katsayi = PAKET_ICI_MAP[paketIci] || 0;
  const paketEni = katsayi > 0 ? round(ebat * katsayi, 0) : 0;
  const paketBoyu = paketEni > 0 ? round(paketEni * 0.877, 0) : 0;
  const enToplam = paketEni > 0 ? round(paketEni * enAdet, 0) : 0;
  const boyToplam = paketBoyu > 0 ? round(paketBoyu * boyAdet, 0) : 0;

  return {
    ...row,
    _base: {
      kalinlik,
      ebat,
      boy,
      paketIci,
      bantEni,
      adet,
      enAdet,
      boyAdet,
      paketKgRaw,
      paketKg,
      paketSayisiRaw,
      paketSayisi,
      kalanPaket,
      totalTons,
      katsayi,
      paketEni,
      paketBoyu,
      enToplam,
      boyToplam,
    },
  };
}

function allocateContainersOptimized(rows, limits) {
  const allocationsByRow = {};
  const containers = [];

  for (const row of rows) {
    allocationsByRow[row.id] = {};
  }

  const packagePool = [];

  for (const row of rows) {
    const base = row._base;

    if (base.paketSayisi <= 0 || base.paketKgRaw <= 0 || base.paketBoyu <= 0) {
      continue;
    }

    const singlePackageFits =
      base.paketKgRaw <= limits.maxTonLimit &&
      base.paketBoyu <= limits.maxBoyLimit;

    if (!singlePackageFits) {
      allocationsByRow[row.id]._error =
        "Bu satırda tek paket bile konteyner limitlerine sığmıyor.";
      continue;
    }

    for (let i = 0; i < base.paketSayisi; i += 1) {
      packagePool.push({
        rowId: row.id,
        paketKg: base.paketKgRaw,
        paketBoyu: base.paketBoyu,
        paketEni: base.paketEni,
        score: Math.max(
          base.paketKgRaw / limits.maxTonLimit,
          base.paketBoyu / limits.maxBoyLimit
        ),
      });
    }
  }

  packagePool.sort((a, b) => b.score - a.score);

  for (const pkg of packagePool) {
    let bestContainerIndex = -1;
    let bestWasteScore = Number.POSITIVE_INFINITY;

    for (let i = 0; i < containers.length; i += 1) {
      const container = containers[i];

      const fitsKg = container.usedKg + pkg.paketKg <= limits.maxTonLimit;
      const fitsBoy = container.usedBoy + pkg.paketBoyu <= limits.maxBoyLimit;

      if (!fitsKg || !fitsBoy) continue;

      const remainingKg = limits.maxTonLimit - (container.usedKg + pkg.paketKg);
      const remainingBoy =
        limits.maxBoyLimit - (container.usedBoy + pkg.paketBoyu);

      const wasteScore =
        remainingKg / limits.maxTonLimit + remainingBoy / limits.maxBoyLimit;

      if (wasteScore < bestWasteScore) {
        bestWasteScore = wasteScore;
        bestContainerIndex = i;
      }
    }

    if (bestContainerIndex === -1) {
      containers.push({
        no: containers.length + 1,
        usedKg: pkg.paketKg,
        usedBoy: pkg.paketBoyu,
        allocations: {
          [pkg.rowId]: {
            packages: 1,
            kg: pkg.paketKg,
          },
        },
      });
    } else {
      const container = containers[bestContainerIndex];
      container.usedKg += pkg.paketKg;
      container.usedBoy += pkg.paketBoyu;

      const existing = container.allocations[pkg.rowId] || {
        packages: 0,
        kg: 0,
      };

      existing.packages += 1;
      existing.kg += pkg.paketKg;

      container.allocations[pkg.rowId] = existing;
    }
  }

  for (const container of containers) {
    for (const [rowId, allocation] of Object.entries(container.allocations)) {
      if (!allocationsByRow[rowId]) {
        allocationsByRow[rowId] = {};
      }

      allocationsByRow[rowId][container.no] = {
        packages: allocation.packages,
        kg: round(allocation.kg, 0),
      };
    }

    container.usedKg = round(container.usedKg, 0);
    container.usedBoy = round(container.usedBoy, 0);
  }

  return {
    containers,
    allocationsByRow,
  };
}

function KonteynerPlanlama() {
  const [rows, setRows] = useState([createEmptyRow()]);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);

  const baseRows = useMemo(() => rows.map(calculateBaseRow), [rows]);

  const allocationResult = useMemo(() => {
    return allocateContainersOptimized(baseRows, limits);
  }, [baseRows, limits]);

  const finalRows = useMemo(() => {
    const { allocationsByRow } = allocationResult;

    return baseRows.map((row) => {
      const rowAllocations = allocationsByRow[row.id] || {};
      return {
        ...row,
        _allocations: rowAllocations,
      };
    });
  }, [baseRows, allocationResult]);

  const maxContainerCount = useMemo(() => {
    return allocationResult.containers.length;
  }, [allocationResult]);

  const totals = useMemo(() => {
    const result = {
      totalTons: 0,
      containerKgTotals: {},
    };

    for (const row of finalRows) {
      result.totalTons += row._base.totalTons;

      for (let i = 1; i <= maxContainerCount; i += 1) {
        const allocation = row._allocations[i];
        if (allocation) {
          result.containerKgTotals[i] =
            (result.containerKgTotals[i] || 0) + allocation.kg;
        }
      }
    }

    result.totalTons = round(result.totalTons, 1);
    return result;
  }, [finalRows, maxContainerCount]);

  const updateRow = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const addEmptyRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const copyRow = (id) => {
    setRows((prev) => {
      const source = prev.find((row) => row.id === id);
      if (!source) return prev;

      const copy = {
        ...source,
        id: Date.now() + Math.random(),
      };

      return [...prev, copy];
    });
  };

  const deleteRow = (id) => {
    setRows((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((row) => row.id !== id);
    });
  };

  const renderContainerHeaders = () => {
    const headers = [];

    for (let i = 1; i <= maxContainerCount; i += 1) {
      headers.push(<th key={`container-${i}`}>{i}. Konteyner</th>);
      headers.push(<th key={`container-kg-${i}`}>Kg</th>);
    }

    return headers;
  };

  const renderContainerCells = (row) => {
    const cells = [];

    for (let i = 1; i <= maxContainerCount; i += 1) {
      const allocation = row._allocations[i];

      cells.push(
        <td key={`row-${row.id}-container-${i}`}>
          {allocation ? formatTr(allocation.packages, 0) : ""}
        </td>
      );

      cells.push(
        <td key={`row-${row.id}-container-kg-${i}`}>
          {allocation ? formatTr(allocation.kg, 0) : ""}
        </td>
      );
    }

    return cells;
  };

  const renderTotalContainerCells = () => {
    const cells = [];

    for (let i = 1; i <= maxContainerCount; i += 1) {
      cells.push(<td key={`total-empty-${i}`}></td>);
      cells.push(
        <td key={`total-kg-${i}`}>
          {formatTr(totals.containerKgTotals[i] || 0, 0)}
        </td>
      );
    }

    return cells;
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold m-0">Konteyner Planlama</h3>
          <small className="text-muted">
            Karışık ürün yükleme, az konteyner ve verimli dağıtım
          </small>
        </div>

        <div className="d-flex gap-2">
          <Button variant="primary" onClick={addEmptyRow}>
            + Boş Satır
          </Button>
        </div>
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="small fw-bold">
                Max Tonaj Limit (kg)
              </Form.Label>
              <Form.Control
                type="number"
                value={limits.maxTonLimit}
                onChange={(e) =>
                  setLimits((prev) => ({
                    ...prev,
                    maxTonLimit: toNum(e.target.value),
                  }))
                }
              />
            </Col>

            <Col md={6}>
              <Form.Label className="small fw-bold">
                Max Boy Limit (mm)
              </Form.Label>
              <Form.Control
                type="number"
                value={limits.maxBoyLimit}
                onChange={(e) =>
                  setLimits((prev) => ({
                    ...prev,
                    maxBoyLimit: toNum(e.target.value),
                  }))
                }
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table
              bordered
              striped
              hover
              size="sm"
              className="mb-0 align-middle"
            >
              <thead className="table-light">
                <tr>
                  <th>KALINLIK</th>
                  <th>EBAT</th>
                  <th>BOY</th>
                  <th>PAKET İÇİ</th>
                  <th>BANT ENİ</th>
                  <th>PAKET KG</th>
                  <th>ADET</th>
                  <th>PAKET SAYISI</th>
                  <th>KALAN PAKET</th>
                  <th>TOTAL TONS</th>
                  {renderContainerHeaders()}
                  <th>paket eni</th>
                  <th>paket boyu</th>
                  <th>en adet</th>
                  <th>en toplam</th>
                  <th>boy adet</th>
                  <th>boy toplam</th>
                  <th>Uyarı</th>
                  <th>İşlem</th>
                </tr>
              </thead>

              <tbody>
                {finalRows.map((row) => (
                  <tr key={row.id}>
                    <td style={{ minWidth: 90 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={row.kalinlik}
                        onChange={(e) =>
                          updateRow(row.id, "kalinlik", e.target.value)
                        }
                      />
                    </td>

                    <td style={{ minWidth: 90 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={row.ebat}
                        onChange={(e) =>
                          updateRow(row.id, "ebat", e.target.value)
                        }
                      />
                    </td>

                    <td style={{ minWidth: 100 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.boy}
                        onChange={(e) =>
                          updateRow(row.id, "boy", e.target.value)
                        }
                      />
                    </td>

                    <td style={{ minWidth: 100 }}>
                      <Form.Select
                        size="sm"
                        value={row.paketIci}
                        onChange={(e) =>
                          updateRow(row.id, "paketIci", e.target.value)
                        }
                      >
                        <option value=""></option>
                        {Object.keys(PAKET_ICI_MAP).map((key) => (
                          <option key={key} value={key}>
                            {key}
                          </option>
                        ))}
                      </Form.Select>
                    </td>

                    <td style={{ minWidth: 100 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={row.bantEni}
                        onChange={(e) =>
                          updateRow(row.id, "bantEni", e.target.value)
                        }
                      />
                    </td>

                    <td>{formatTr(row._base.paketKg, 0)}</td>

                    <td style={{ minWidth: 100 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.adet}
                        onChange={(e) =>
                          updateRow(row.id, "adet", e.target.value)
                        }
                      />
                    </td>

                    <td>{formatTr(row._base.paketSayisi, 1)}</td>
                    <td>{formatTr(row._base.kalanPaket, 0)}</td>
                    <td>{formatTr(row._base.totalTons, 1)}</td>

                    {renderContainerCells(row)}

                    <td>{formatTr(row._base.paketEni, 0)}</td>
                    <td>{formatTr(row._base.paketBoyu, 0)}</td>

                    <td style={{ minWidth: 80 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.enAdet}
                        onChange={(e) =>
                          updateRow(row.id, "enAdet", e.target.value)
                        }
                      />
                    </td>

                    <td>{formatTr(row._base.enToplam, 0)}</td>

                    <td style={{ minWidth: 80 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.boyAdet}
                        onChange={(e) =>
                          updateRow(row.id, "boyAdet", e.target.value)
                        }
                      />
                    </td>

                    <td>{formatTr(row._base.boyToplam, 0)}</td>

                    <td style={{ minWidth: 220 }}>
                      {row._allocations._error ? (
                        <Alert variant="danger" className="py-1 px-2 m-0">
                          {row._allocations._error}
                        </Alert>
                      ) : (
                        ""
                      )}
                    </td>

                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => copyRow(row.id)}
                          title="Satırı kopyala"
                        >
                          +
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => deleteRow(row.id)}
                          title="Satırı sil"
                        >
                          -
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}

                <tr className="table-secondary fw-bold">
                  <td colSpan="9" className="text-end">
                    Toplam
                  </td>
                  <td>{formatTr(totals.totalTons, 1)}</td>
                  {renderTotalContainerCells()}
                  <td colSpan="8"></td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </Layout>
  );
}

export default KonteynerPlanlama;
